//BootstrapStarterBody.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
    useToolToggleListeners,
    useClickOffMainMenu,
    useClickOffMobileTools,
    handleReturnToTop,
    handleScrollToBottom,
    hideAllTools,
    hideMainMenu
} from './BootstrapStarterBodyUtilities';
import BootstrapStarterCard from './BootstrapStarterCard';
import ToolTagManagerTool from './ToolTagManagerTool';
import Table from './Table';
import ManagerFilters from './ManagerFilters';
import { useNavigate } from 'react-router-dom';


import 'bootstrap/dist/css/bootstrap.min.css';
import { Button } from 'react-bootstrap';
import './WriterBody.css';
import './CSSToolViewManagerTool.css';
import './CSSToolFiltersManagerTool.css';
import './CSSToolStylesManagerTool.css';
import './CSSMainMenu.css';
import './CSSNavigationButtons.css';
import './ColorMode.css'

const BootstrapStarterBody = ({ writerMode, setWriterMode, colorMode, setColorMode, onTagCountChange }) => {
    const [tagLibraryCount, setTagLibraryCount] = useState(0);
    const navigate = useNavigate();

    const handleTagCountChange = (count) => {
        setTagLibraryCount(count);
        if (onTagCountChange) {
            onTagCountChange(count);
        }
    };
    const [savedStylesForLoading, setSavedStylesForLoading] = useState([]);
    const handleReceiveSavedStylesFromTable = (savedStylesForLoading) => {
        console.log("BootstrapStarterBody got savedStylesForLoading from Table's Loaded File:", savedStylesForLoading);

        // Delay passing to the child component by 1 second
        setTimeout(() => {
            console.log("Passing savedStylesForLoading to BootstrapStarterCard:", savedStylesForLoading);
            setSavedStylesForLoading(savedStylesForLoading); // Save the styles to state
        }, 1000);
    };
    // Inside your component
    const tableRef = useRef(null); // Create a ref for the Table component
    const handleReceiveSavedStyles = (data) => {
        setSavedStylesForLoading(data);

        // ✅ Call Table's internal handler if available
        if (tableRef.current && typeof tableRef.current.handleReceiveSavedStylesInTable === 'function') {
            tableRef.current.handleReceiveSavedStylesInTable(data);
        }
    };

    // Other state management
    const [listItems, setListItems] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [rows, setRows] = useState([{ id: 1, position: 1 }]);
    const [columns, setColumns] = useState([{ id: 1, position: 1, name: '' }]);

    const [visibleTool, setVisibleTool] = useState(null);
    const containerRef = useRef(null);  // Create a reference for the container
    useToolToggleListeners(setVisibleTool);
    useClickOffMainMenu(visibleTool, setVisibleTool);
    useClickOffMobileTools(visibleTool, setVisibleTool);

    const clickSaveFileButton = () => {
        const saveButton = document.getElementById('buttonSaveFile');
        if (saveButton) {
            saveButton.click();
        } else {
            console.log('Save button not found');
        }
    };

    const clickLoadFileButton = () => {
        const loadButton = document.getElementById('buttonLoadFile');
        if (loadButton) {
            loadButton.click();
        } else {
            console.log('Load button not found');
        }
    };
    const clickLoadTemplateButton = () => {
        const loadTemplateButton = document.getElementById('buttonLoadTemplate');
        if (loadTemplateButton) {
            loadTemplateButton.click();
        } else {
            console.log('Load Template button not found');
        }
    };

    const savedStylesHide = () => {
        const targetDiv = document.getElementById("toolSavedStylesContainerPosition");
        if (targetDiv) {
            targetDiv.style.visibility = "hidden";
        }
    };
    const savedStylesShow = () => {
        const targetDiv = document.getElementById("toolSavedStylesContainerPosition");
        if (targetDiv) {
            targetDiv.style.visibility = "visible";
        }
    };
    const savedStylesShowClosesViewsTagsStylesFilters = () => {
        console.log('savedStylesShowClosesViewsTagsStylesFilters triggered');

        // Find all buttons with the specific ID and click them
        const buttons = document.querySelectorAll('button#closeOpenToolManager');
        buttons.forEach((button) => {
            button.click();
        });
    };
    const findAndClickStyleButtonInHeader = () => {
        const button = document.getElementById("appHeaderBarColRight-BoxLogoMenu3of5Button");
        if (button) {
            button.click();
        } else {
            console.warn("Style button in header not found: #appHeaderBarColRight-BoxLogoMenu3of5Button");
        }
    };
    const projectNamePreserver = () => {
        const input = document.getElementById('fileNameInput');
        const previousValue = input?.value;

        if (previousValue) {
            console.log(`📁 Preserving file name: "${previousValue}"`);
            setTimeout(() => {
                const newInput = document.getElementById('fileNameInput');
                if (newInput) {
                    newInput.value = previousValue;
                    newInput.dispatchEvent(new Event('input', { bubbles: true }));
                    console.log(`📁 Rehydrated file name: "${previousValue}"`);
                }
            }, 100); // Delay to wait for re-render
        }
    };

    // Add this useEffect to listen for tag count update requests
    useEffect(() => {
        const handleTagCountUpdateRequest = () => {
            console.log('BootstrapStarterBody received request to update tag count, current count:', listItems.length);
            if (onTagCountChange) {
                onTagCountChange(listItems.length);
            }
        };

        window.addEventListener('requestTagCountUpdate', handleTagCountUpdateRequest);

        return () => {
            window.removeEventListener('requestTagCountUpdate', handleTagCountUpdateRequest);
        };
    }, [listItems, onTagCountChange]);

    // Also trigger update when listItems changes (like when loading a file)
    useEffect(() => {
        if (onTagCountChange) {
            projectNamePreserver(); // Preserve the file name before triggering updates
            onTagCountChange(listItems.length);
        }
    }, [listItems, onTagCountChange]);

    const closeAllExpandedAnnotations = () => {
        // Get all annotation column containers (column 5s)
        const annotationCols = document.querySelectorAll('[id^="appBodyCol5-"]');

        annotationCols.forEach(col => {
            const isExpanded = col.style.flexBasis === '65%' || col.classList.contains('annotation-grow');

            if (isExpanded) {
                // Find the closest parent row
                const row = col.closest('.cardSpacer');
                if (!row) return;

                // Find the toggle button within that row
                const toggleButton = row.querySelector('.buttonAnnotationRevealerContainer');

                if (toggleButton) {
                    toggleButton.click();
                }
            }
        });
    };
    const openAllHiddenAnnotations = () => {
        // Get all annotation column containers (column 5s)
        const annotationCols = document.querySelectorAll('[id^="appBodyCol5-"]');

        annotationCols.forEach(col => {
            const isCollapsed = col.style.flexBasis !== '65%' && !col.classList.contains('annotation-grow');

            if (isCollapsed) {
                // Find the closest parent row
                const row = col.closest('.cardSpacer');
                if (!row) return;

                // Find the toggle button within that row
                const toggleButton = row.querySelector('.buttonAnnotationRevealerContainer');

                if (toggleButton) {
                    toggleButton.click();
                }
            }
        });
    };
    const setAllCardsToFixedHeight = () => {
        // Select all matching column2 divs with cardInstanceId pattern
        const columnContainers = document.querySelectorAll('div[id^="appBodyCol2-"].gray.column2of5.animated-column-transition');

        columnContainers.forEach(container => {
            // Within each col2 container, find text boxes
            const textBoxContainers = container.querySelectorAll('.reactQuillForTextBoxContainer');

            textBoxContainers.forEach(textBox => {
                const isExpanded = textBox.classList.contains('auto-height');
                if (isExpanded) {
                    const toggleButton = textBox.querySelector('#buttonToggleFixedHeightAutoHieght');
                    if (toggleButton) {
                        toggleButton.click();
                    }
                }
            });
        });
    };
    const setAllCardsToFullHeight = () => {
        // Select all matching column2 divs with cardInstanceId pattern
        const columnContainers = document.querySelectorAll('div[id^="appBodyCol2-"].gray.column2of5.animated-column-transition');

        columnContainers.forEach(container => {
            // Within each col2 container, find text boxes
            const textBoxContainers = container.querySelectorAll('.reactQuillForTextBoxContainer');

            textBoxContainers.forEach(textBox => {
                const isExpanded = textBox.classList.contains('fixed-height');
                if (isExpanded) {
                    const toggleButton = textBox.querySelector('#buttonToggleFixedHeightAutoHieght');
                    if (toggleButton) {
                        toggleButton.click();
                    }
                }
            });
        });
    };

    const [triggerProjectReporter, setTriggerProjectReporter] = useState(0);

    const projectReportStart = () => {
        console.log("projectReportStart was called.");
        // Trigger the child component's function by updating state
        setTriggerProjectReporter(prev => prev + 1);
    };

    useEffect(() => {
        if (visibleTool === 'filters') {
            projectReportStart();
        }
    }, [visibleTool]);

    const findClickHeaderButtonSend = () => {
        const targetButton = document.getElementById('appHeaderBarColRight-BoxLogoMenu5of5Button');

        if (targetButton) {
            console.log('Found Send button, clicking...');
            targetButton.click();
        } else {
            console.warn('appHeaderBarColRight-BoxLogoMenu5of5Button not found in DOM');
        }
    };

    return (
        <div className="makeMeGreen appBody">
            <Button
                id="buttonSavedStylesHide"
                onClick={savedStylesHide}
                style={{ display: 'none' }}
            >
                HIDE Saved Styles
            </Button>
            <Button
                id="buttonSavedStylesShow"
                onClick={() => {
                    savedStylesShow();
                }}
                style={{ display: 'none' }}
            >
                SHOW Saved Styles
            </Button>
            <Button
                id="buttonHideViewsTagsFilters"
                onClick={savedStylesShowClosesViewsTagsStylesFilters}
                style={{ display: 'none' }}
            >
                HIDE Views/Tags/Filters
            </Button>

            <Table
                rows={rows}
                setRows={setRows}
                columns={columns}
                setColumns={setColumns}
                listItems={listItems}
                setListItems={setListItems}
                ref={tableRef}
                savedStyles={savedStylesForLoading}
                onReceiveSavedStyles={handleReceiveSavedStylesFromTable}
            />

            <div className={
                (writerMode === 'Doc' ? 'view-doc' :
                    writerMode === 'Note Pad' ? 'view-pad' :
                        writerMode === 'Sticky Note' ? 'view-sticky' : '') +
                (colorMode === 'Neutral' ? ' view-neutral' :
                    colorMode === 'Dark' ? ' view-dark' : '')
            }>
                <div id="bodyTextBox" className="colorTest">
                    {/* Body Test Box */}
                </div>
                <div id="appBodyBarRow" className="row red">
                    <div id="appBodyBarRowStyleBarBox" className="appBodyBarRowStyleBarBox"></div>
                    <div
                        id="appBodyColumnWriter"
                        className="col-12 col-md notblue appBodyColumnWriterMarginTop makeMeBlue"
                    >


                        <div className="bootstrapStarterCardUnScrollable">


                            <div id="bootstrapStarterCardHeaderDesktop"
                                className="bootstrapStarterCardHeader bootstrapStarterCardHeaderDesktop makeMeRed"
                            >
                                <div id="bootstrapStarterCardHeaderCol1"
                                    className="col-1 col1MarginRightFix">


                                    <div id="columnBoxCardOptions" className="columnBoxCardOptions">
                                        <div id="columnBoxCardOptionsText" className="columnBoxCardOptionsText">
                                            Options
                                        </div>
                                    </div>
                                </div>


                                <div id="bootstrapStarterCardHeaderCol2"
                                    className="col-6 col2MarginRightFix">


                                    <div id="columnBoxCard" className="columnBoxCard columnBoxCardFix">
                                        <div id="columnBoxCardText" className="columnBoxCardText">
                                            Card Column
                                        </div>
                                    </div>

                                    <div id="columnBoxCard2" className="columnBoxCard2Desktop columnBoxCardFix showsInWriterModeOnly">
                                        <div
                                            id="columnBoxCardText"
                                            className="columnBoxCardText"
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '0 8px',
                                                fontFamily: 'monospace',
                                                fontSize: '.9rem',
                                                lineHeight: '1',
                                                userSelect: 'none',
                                            }}
                                        >
                                            {Array.from({ length: 29 }).map((_, index) => {
                                                const isFull = index % 4 === 0 || index === 28;
                                                return (
                                                    <span
                                                        key={index}
                                                        style={{
                                                            transform: isFull ? 'scaleY(1)' : 'scaleY(0.5)',
                                                            display: 'inline-block',
                                                            transformOrigin: 'bottom center',
                                                        }}
                                                    >
                                                        |
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>


                                <div id="bootstrapStarterCardHeaderCol3And4ContainerOuter"
                                    className="bootstrapStarterCardHeaderCol3And4ContainerDesktop col-3 ">


                                    <div id="bootstrapStarterCardHeaderCol3And4ContainerInner" className="">
                                        <div id="bootstrapStarterCardHeaderCol3" className="col-md-12 bootstrapStarterCardHeaderCol3">
                                            <div id="columnBoxTags" className="columnBoxTags">
                                                <div id="columnBoxTagsText" className="columnBoxTagsText">
                                                    Tag Column
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>


                                <div id="bootstrapStarterCardHeaderCol5"
                                    className="col-2">


                                    <div id="columnBoxAnnotation" className="columnBoxAnnotation">
                                        <div id="columnBoxAnnotationText" className="columnBoxAnnotationText">
                                            Annotation
                                        </div>
                                    </div>
                                </div>
                            </div>




                            <div id="bootstrapStarterCardHeaderMobile"
                                className="bootstrapStarterCardHeader row bootstrapStarterCardHeaderMobile makeMeOrange"
                            >
                                <div id="bootstrapStarterCardHeaderCol1" className="col-1">
                                    <div id="columnBoxCardOptions" className="columnBoxCardOptions">
                                        <div id="columnBoxCardOptionsText" className="columnBoxCardOptionsText">
                                            Options Mobile
                                        </div>
                                    </div>
                                </div>
                                <div id="bootstrapStarterCardHeaderCol2" className="col-6">

                                    <div id="columnBoxCard" className="columnBoxCard">
                                        <div id="columnBoxCardText" className="columnBoxCardText">
                                            Card Column Mobile
                                        </div>
                                    </div>

                                    <div id="columnBoxCardMobile" className="columnBoxCard2Mobile columnBoxCardFix showsInWriterModeOnly">
                                        <div
                                            id="columnBoxCardText"
                                            className="columnBoxCardText"
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                padding: '0 8px',
                                                fontFamily: 'monospace',
                                                fontSize: '.9rem',
                                                lineHeight: '1',
                                                userSelect: 'none',
                                            }}
                                        >
                                            {Array.from({ length: 21 }).map((_, index) => {
                                                const isFull = index % 4 === 0 || index === 20;
                                                return (
                                                    <span
                                                        key={index}
                                                        style={{
                                                            transform: isFull ? 'scaleY(1)' : 'scaleY(0.5)',
                                                            display: 'inline-block',
                                                            transformOrigin: 'bottom center',
                                                        }}
                                                    >
                                                        |
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>





                                </div>


                                <div id="bootstrapStarterCardHeaderCol5" className="col-3">
                                    <div id="columnBoxAnnotation" className="columnBoxAnnotation">
                                        <div id="columnBoxAnnotationText" className="columnBoxAnnotationText">
                                            Annotation Column Mobile
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>


                        <div className="bootstrapStarterCardScrollable" ref={containerRef}>



                            <BootstrapStarterCard
                                hideAllTools={hideAllTools}
                                hideMainMenu={hideMainMenu}
                                writerMode={writerMode}
                                colorMode={colorMode}
                                rows={rows}
                                setRows={setRows}
                                columns={columns}
                                setColumns={setColumns}
                                listItems={listItems}
                                setListItems={setListItems}
                                onReceiveSavedStyles={handleReceiveSavedStyles}
                                savedStylesForLoading={savedStylesForLoading}

                            />

                            {/* Navigation Buttons */}
                            <div
                                id="navigationButtonsContainer"
                                className="navigationButtonsContainer"
                            >
                                <Button
                                    id="buttonScrollUp"
                                    onClick={() => handleReturnToTop(containerRef)}
                                    size="sm"
                                    className="buttonScroll buttonBottomNavColorMode"

                                >
                                    <div
                                        id="buttonScrollUpBox"
                                        className="buttonScrollBox"
                                    >
                                        <div
                                            id="buttonScrollUpBoxImage"
                                            className="buttonScrollBoxImage"
                                        >
                                            <img
                                                id="buttonScrollUpBoxImageImg"
                                                src="/SVGicons/scroll-up-black.svg"
                                                alt="Scroll UP to First Card"
                                            />
                                        </div>
                                        <div
                                            id="buttonScrollUpBoxText"
                                            className="buttonScrollBoxText"
                                        >
                                            <span className="button-text">SCROLL UP to First Card</span>
                                        </div>
                                    </div>
                                </Button>

                                <Button
                                    id="buttonScrollDown"
                                    onClick={() => handleScrollToBottom(containerRef)}
                                    size="sm"
                                    className="buttonScroll buttonBottomNavColorMode"

                                >
                                    <div
                                        id="buttonScrollDownBox"
                                        className="buttonScrollBox"
                                    >
                                        <div
                                            id="buttonScrollDownBoxImage"
                                            className="buttonScrollBoxImage"
                                        >
                                            <img
                                                id="buttonScrollDownBoxImageImg"
                                                src="/SVGicons/scroll-down-black.svg"
                                                alt="Scroll DOWN to Last Card"
                                            />
                                        </div>
                                        <div
                                            id="buttonScrollDownBoxText"
                                            className="buttonScrollBoxText"
                                        >
                                            <span className="button-text">SCROLL DOWN to Last Card</span>
                                        </div>
                                    </div>
                                </Button>
                                <Button
                                    id="buttonShowAllAnnotations"
                                    onClick={() => openAllHiddenAnnotations()}
                                    size="sm"
                                    className="buttonScroll buttonBottomNavColorMode"
                                >
                                    <div
                                        id="buttonShowAllAnnotationsBox"
                                        className="buttonScrollBox"
                                    >
                                        <div
                                            id="buttonShowAllAnnotationsBoxImage"
                                            className="buttonScrollBoxImage"
                                        >
                                            <img
                                                id="buttonShowAllAnnotationsBoxImageImg"
                                                src="/SVGicons/annotation-expand-all.svg"
                                                alt="Close All Expanded Annotations"
                                            />
                                        </div>
                                        <div
                                            id="buttonShowAllAnnotationsBoxText"
                                            className="buttonScrollBoxText"
                                        >
                                            <span className="button-text">SHOW ALL Annotations</span>
                                        </div>
                                    </div>
                                </Button>

                                <Button
                                    id="buttonHideAllAnnotations"
                                    onClick={() => closeAllExpandedAnnotations()}
                                    size="sm"
                                    className="buttonScroll buttonBottomNavColorMode"
                                >
                                    <div
                                        id="buttonHideAllAnnotationsBox"
                                        className="buttonScrollBox"
                                    >
                                        <div
                                            id="buttonHideAllAnnotationsBoxImage"
                                            className="buttonScrollBoxImage"
                                        >
                                            <img
                                                id="buttonHideAllAnnotationsBoxImageImg"
                                                src="/SVGicons/annotation-collapse-all.svg"
                                                alt="Close All Expanded Annotations"
                                            />
                                        </div>
                                        <div
                                            id="buttonHideAllAnnotationsBoxText"
                                            className="buttonScrollBoxText"
                                        >
                                            <span className="button-text">HIDE ALL Annotations</span>
                                        </div>
                                    </div>
                                </Button>
                                <Button
                                    id="buttonSetAllCardsToFixedHeight"
                                    onClick={() => setAllCardsToFixedHeight()}
                                    size="sm"
                                    className="buttonScroll buttonBottomNavColorMode"
                                >
                                    <div
                                        id="buttonSetAllCardsToFixedHeightBox"
                                        className="buttonScrollBox"
                                    >
                                        <div
                                            id="buttonSetAllCardsToFixedHeightBoxImage"
                                            className="buttonScrollBoxImage"
                                        >
                                            <img
                                                id="buttonSetAllCardsToFixedHeightBoxImageImg"
                                                src="/SVGicons/height-fixed-black-all.svg"
                                                alt="Scroll DOWN to Last Card"
                                            />
                                        </div>
                                        <div
                                            id="buttonSetAllCardsToFixedHeightBoxText"
                                            className="buttonScrollBoxText"
                                        >
                                            <span className="button-text">SET ALL Cards to Fixed Height</span>
                                        </div>
                                    </div>
                                </Button>
                                <Button
                                    id="buttonExpandAllCardsToFullHeight"
                                    onClick={() => setAllCardsToFullHeight()}
                                    size="sm"
                                    className="buttonScroll buttonBottomNavColorMode"
                                >
                                    <div
                                        id="buttonExpandAllCardsToFullHeightBox"
                                        className="buttonScrollBox"
                                    >
                                        <div
                                            id="buttonExpandAllCardsToFullHeightBoxImage"
                                            className="buttonScrollBoxImage"
                                        >
                                            <img
                                                id="buttonExpandAllCardsToFullHeightBoxImageImg"
                                                src="/SVGicons/height-full-black-all.svg"
                                                alt="Scroll DOWN to Last Card"
                                            />
                                        </div>
                                        <div
                                            id="buttonExpandAllCardsToFullHeightBoxText"
                                            className="buttonScrollBoxText"
                                        >
                                            <span className="button-text">SET ALL Cards to Auto Height</span>
                                        </div>
                                    </div>
                                </Button>
                            </div>

                        </div>

                    </div>

                    {visibleTool && (
                        <div id="appBodyColumnTools" className="app-body-tools-container yellow d-none d-md-block">
                            <div id="appBodyColumnToolsRow" className="row salmon">

                                <div id="appBodyToolMainMenu"
                                    className={`col-md-12 gray stacked-menu appBodyToolMainMenuContainer ${visibleTool === 'mainmenu' ? 'force-mobile-style' : 'd-none'}`}
                                >
                                    <div className="d-flex flex-column gap-2 ">


                                        <div
                                            id="mainMenuTitleContainer"
                                            className="mainMenuTitleContainer"
                                        >
                                            <div
                                                id="mainMenuTitleContainerTextBox"
                                                className="mainMenuTitleContainerTextBox"
                                            >
                                                <p
                                                    id="mainMenuTitle" className="mainMenuTitle"
                                                >
                                                    Main Menu
                                                </p>

                                            </div>
                                            <div
                                                id="mainMenuTitleContainerCloseButtonBox"
                                                className="mainMenuTitleContainerCloseButtonBox"
                                            >
                                                <Button
                                                    id="closeMainMenu"
                                                    className="closeMainMenu"
                                                    onClick={() => setVisibleTool(null)}
                                                >
                                                    <img
                                                        id="closeMainMenuImg"
                                                        className="closeMainMenuImg"
                                                        src="/SVGicons/X-white.svg"
                                                        alt="Close"
                                                        style={{ width: '12px', height: '12px' }}
                                                    />
                                                </Button>
                                            </div>
                                        </div>


                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxNewProject"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={() => {
                                                clickSaveFileButton();
                                                window.location.reload();
                                                setVisibleTool(null);
                                            }}
                                        >
                                            <div
                                                id="mainMenuButtonBoxNewProject"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageNewProject"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconNewProject"
                                                        src="/SVGicons/project-new-black.svg"
                                                        alt="NEW Project"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextNewProject"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">NEW Project</span>
                                                </div>
                                            </div>
                                        </Button>
                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxOpenProject"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={() => {
                                                clickLoadFileButton();
                                                setVisibleTool(null);
                                            }}
                                        >
                                            <div
                                                id="mainMenuButtonBoxOpenProject"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageOpenProject"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconOpenProject"
                                                        src="/SVGicons/project-open-black.svg"
                                                        alt="OPEN Project"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextOpenProject"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">OPEN Project</span>
                                                </div>
                                            </div>
                                        </Button>
                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxOpenTemplate"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={() => {
                                                clickLoadTemplateButton();
                                                setVisibleTool(null);
                                            }}
                                        >
                                            <div
                                                id="mainMenuButtonBoxOpenTemplate"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageOpenTemplate"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconOpenTemplat"
                                                        src="/SVGicons/template-open-black.svg"
                                                        alt="OPEN Template"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextOpenProject"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">OPEN Template</span>
                                                </div>
                                            </div>
                                        </Button>
                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxSaveProject"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={() => {
                                                clickSaveFileButton();
                                                setVisibleTool(null);
                                            }}
                                        >
                                            <div
                                                id="mainMenuButtonBoxSaveProject"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageSaveProject"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconSaveProject"
                                                        src="/SVGicons/project-save-black.svg"
                                                        alt="SAVE Project"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextSaveProject"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">SAVE Project</span>
                                                </div>
                                            </div>
                                        </Button>

                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxSend"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={findClickHeaderButtonSend}
                                        >
                                            <div
                                                id="mainMenuButtonBoxSend"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageSend"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconSend"
                                                        src="/SVGicons/send-black.svg"
                                                        alt="Send"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextSend"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">Send</span>
                                                </div>
                                            </div>
                                        </Button>
                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxSupport"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={() => {
                                                setVisibleTool(null); // existing logic
                                                navigate('/learning'); // navigate to PageSupporting
                                            }}
                                        >
                                            <div
                                                id="mainMenuButtonBoxSupport"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageSupport"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconSupport"
                                                        src="/SVGicons/help-black.svg"
                                                        alt="Help"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextSupport"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">Support</span>
                                                </div>
                                            </div>
                                        </Button>
                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxAccount"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={() => {
                                                setVisibleTool(null); // existing logic
                                                navigate('/account'); // navigate to PageSupporting
                                            }}
                                        >
                                            <div
                                                id="mainMenuButtonBoxAccount"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageAccount"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconAccount"
                                                        src="/SVGicons/account-black.svg"
                                                        alt="Help"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextAccount"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">Account</span>
                                                </div>
                                            </div>
                                        </Button>


                                        <Button
                                            size="sm"
                                            id="mainMenuButtonBoxLogOut"
                                            className="responsive-icon-button mainMenuButton"
                                            onClick={() => {
                                                setVisibleTool(null); // existing logic
                                                navigate('/account'); // navigate to PageSupporting
                                            }}
                                        >
                                            <div
                                                id="mainMenuButtonBoxLogOut"
                                                className="mainMenuButtonBox"
                                            >
                                                <div
                                                    id="mainMenuButtonBoxImageLogOut"
                                                    className="mainMenuButtonBoxImage"
                                                >
                                                    <img
                                                        id="iconLogOut"
                                                        src="/SVGicons/logout-black.svg"
                                                        alt="Help"
                                                    />
                                                </div>
                                                <div
                                                    id="mainMenuButtonBoxTextLogOut"
                                                    className="mainMenuButtonBoxText"
                                                >
                                                    <span className="button-text">Log Out</span>
                                                </div>
                                            </div>
                                        </Button>



                                    </div>
                                </div>

                                {/* View Toggle Menu */}
                                <div id="appBodyToolViews"
                                    className={`col-md-12 gray ${visibleTool === 'views' ? '' : 'd-none'}`}
                                >
                                    <div
                                        id="viewManagerToolContainer"
                                        className="viewManagerToolContainer"
                                    >


                                        <div
                                            id="viewManagerToolContainerTitleContainer"
                                            className="viewManagerToolContainerTitleContainer"
                                        >
                                            <div
                                                id="viewManagerToolContainerTitleContainerCol01IconBox"
                                                className="viewManagerToolContainerTitleContainerCol01IconBox"
                                            >
                                                <img
                                                    id="viewManagerToolContainerTitleBoxIconImg"
                                                    src="/SVGicons/views-black.svg"
                                                    alt="Save Tag"
                                                    style={{ width: '24px', height: '24px' }}
                                                />
                                            </div>
                                            <div
                                                id="viewManagerToolContainerTitleContainerCol02TitleBox"
                                                className="viewManagerToolContainerTitleContainerCol02TitleBox"
                                            >
                                                <p
                                                    id="toolManagerTitleViews"
                                                >
                                                    Views Manager
                                                </p>

                                            </div>
                                            <div
                                                id="viewManagerToolContainerTitleContainerCol03ButtonBox"
                                                className="viewManagerToolContainerTitleContainerCol03ButtonBox"
                                            >
                                                <Button
                                                    id="closeOpenToolManager"
                                                    className="closeOpenToolManager"
                                                    onClick={() => setVisibleTool(null)}
                                                >
                                                    <img
                                                        id="closeOpenToolManagerImg"
                                                        className="closeOpenToolManagerImg"
                                                        src="/SVGicons/X-white.svg"
                                                        alt="Close"
                                                        style={{ width: '12px', height: '12px' }}
                                                    />
                                                </Button>
                                            </div>
                                        </div>






                                        {/* Writer Mode Toggle Tool */}
                                        <div
                                            id="viewManagerToolContainerMenuTitleContainerWriterMode"
                                            className="mt-4 viewManagerToolContainerMenuTitleContainer"
                                        >
                                            <p>Writer Mode</p>

                                            <div
                                                id="viewManagerToolContainerToggleContainerWriterMode"
                                                className="viewManagerToolContainerToggleContainer"
                                            >
                                                {/* Labels with icons above the slider */}
                                                <div id="writer-toggle-labels-container" className="writer-toggle-labels-container">
                                                    <div className="writer-label-with-icon">
                                                        <span>Sheet</span>
                                                        <img
                                                            src={
                                                                writerMode === 'Sheet'
                                                                    ? '/SVGicons/sheet-black-gold.svg'
                                                                    : '/SVGicons/sheet-black.svg'
                                                            }
                                                            alt="Sheet Icon"
                                                        />
                                                    </div>
                                                    <div className="writer-label-with-icon">
                                                        <span>Doc</span>
                                                        <img
                                                            src={
                                                                writerMode === 'Doc'
                                                                    ? '/SVGicons/doc-black-gold.svg'
                                                                    : '/SVGicons/doc-black.svg'
                                                            }
                                                            alt="Doc Icon"
                                                        />
                                                    </div>
                                                    <div className="writer-label-with-icon">
                                                        <span>Pad</span>
                                                        <img
                                                            src={
                                                                writerMode === 'Note Pad'
                                                                    ? '/SVGicons/pad-black-gold.svg'
                                                                    : '/SVGicons/notepad-black.svg'
                                                            }
                                                            alt="Pad Icon"
                                                        />
                                                    </div>
                                                    <div className="writer-label-with-icon">
                                                        <span>Sticky</span>
                                                        <img
                                                            src={
                                                                writerMode === 'Sticky Note'
                                                                    ? '/SVGicons/sticky-black-gold.svg'
                                                                    : '/SVGicons/sticky-black.svg'
                                                            }
                                                            alt="Sticky Icon"
                                                        />
                                                    </div>
                                                </div>


                                                {/* The slider toggle itself */}
                                                <div
                                                    id="writer-toggle-switch"
                                                    className="writer-toggle-switch"
                                                >

                                                    <input
                                                        type="radio"
                                                        id="modeSheet"
                                                        name="writerMode"
                                                        checked={writerMode === 'Sheet'}
                                                        onChange={() => {
                                                            projectNamePreserver();
                                                            setWriterMode('Sheet');
                                                        }}
                                                    />
                                                    <label htmlFor="modeSheet"></label>

                                                    <input
                                                        type="radio"
                                                        id="modeDoc"
                                                        name="writerMode"
                                                        checked={writerMode === 'Doc'}
                                                        onChange={() => {
                                                            projectNamePreserver();
                                                            setWriterMode('Doc');
                                                        }}
                                                    />
                                                    <label htmlFor="modeDoc"></label>

                                                    <input
                                                        type="radio"
                                                        id="modeNotePad"
                                                        name="writerMode"
                                                        checked={writerMode === 'Note Pad'}
                                                        onChange={() => {
                                                            projectNamePreserver();
                                                            setWriterMode('Note Pad');
                                                        }}
                                                    />
                                                    <label htmlFor="modeNotePad"></label>

                                                    <input
                                                        type="radio"
                                                        id="modeStickyNote"
                                                        name="writerMode"
                                                        checked={writerMode === 'Sticky Note'}
                                                        onChange={() => {
                                                            projectNamePreserver();
                                                            setWriterMode('Sticky Note');
                                                        }}
                                                    />
                                                    <label htmlFor="modeStickyNote"></label>


                                                    {/* Ghost Circles */}
                                                    <div className="writer-ghost-circle sheet" />
                                                    <div className="writer-ghost-circle doc" />
                                                    <div className="writer-ghost-circle notepad" />
                                                    <div className="writer-ghost-circle stickynote" />

                                                    <div className="writer-slider" />
                                                </div>
                                            </div>

                                        </div>


                                        {/* Color Mode Toggle Tool */}
                                        <div
                                            id="viewManagerToolContainerMenuTitleContainerColorMode"
                                            className="mt-4 viewManagerToolContainerMenuTitleContainer"
                                        >
                                            <p>Color Mode</p>

                                            <div
                                                id="viewManagerToolContainerToggleContainerColorMode"
                                                className="viewManagerToolContainerToggleContainer"
                                            >


                                                {/* Labels with icons above the slider */}
                                                <div className="toggle-switch-labels-container">
                                                    <div className="label-with-icon">
                                                        <span>Light</span>
                                                        <img
                                                            src={
                                                                colorMode === 'Light'
                                                                    ? '/SVGicons/sun-black-gold.svg'
                                                                    : '/SVGicons/sun-black.svg'
                                                            }
                                                            alt="Light Icon"
                                                        />
                                                    </div>
                                                    <div className="label-with-icon">
                                                        <span>Neutral</span>
                                                        <img
                                                            src={
                                                                colorMode === 'Neutral'
                                                                    ? '/SVGicons/cloud-sun-black-gold.svg'
                                                                    : '/SVGicons/cloud-sun-black.svg'
                                                            }
                                                            alt="Neutral Icon"
                                                        />
                                                    </div>
                                                    <div className="label-with-icon">
                                                        <span>Dark</span>
                                                        <img
                                                            src={
                                                                colorMode === 'Dark'
                                                                    ? '/SVGicons/moon-black-gold.svg'
                                                                    : '/SVGicons/moon-black.svg'
                                                            }
                                                            alt="Dark Icon"
                                                        />
                                                    </div>
                                                </div>


                                                {/* The slider toggle itself */}
                                                <div className="toggle-switch">
                                                    <input
                                                        type="radio"
                                                        id="colorLight"
                                                        name="colorMode"
                                                        checked={colorMode === 'Light'}
                                                        onChange={() => {
                                                            projectNamePreserver();
                                                            setColorMode('Light');
                                                        }}
                                                    />
                                                    <label htmlFor="colorLight"></label>

                                                    <input
                                                        type="radio"
                                                        id="colorNeutral"
                                                        name="colorMode"
                                                        checked={colorMode === 'Neutral'}
                                                        onChange={() => {
                                                            projectNamePreserver();
                                                            setColorMode('Neutral');
                                                        }}
                                                    />
                                                    <label htmlFor="colorNeutral"></label>

                                                    <input
                                                        type="radio"
                                                        id="colorDark"
                                                        name="colorMode"
                                                        checked={colorMode === 'Dark'}
                                                        onChange={() => {
                                                            projectNamePreserver();
                                                            setColorMode('Dark');
                                                        }}
                                                    />
                                                    <label htmlFor="colorDark"></label>

                                                    {/* Ghost Circles */}
                                                    <div className="ghost-circle light" />
                                                    <div className="ghost-circle neutral" />
                                                    <div className="ghost-circle dark" />

                                                    <div className="slider" />
                                                </div>


                                            </div>
                                        </div>






                                    </div>
                                </div>

                                {/* Other tool sections */}
                                <div id="appBodyToolTags"
                                    className={`col-md-12 gray ${visibleTool === 'tags' ? '' : 'd-none'}`}
                                >
                                    <div
                                        id="tagManagerToolContainerOUTTER"
                                        className="tagManagerToolContainer"
                                    >

                                        <div
                                            id="tagManagerToolContainerTitleContainer"
                                            className="tagManagerToolContainerTitleContainer"
                                        >
                                            <div
                                                id="tagManagerToolContainerTitleContainerCol03ButtonBox"
                                                className="tagManagerToolContainerTitleContainerCol03ButtonBox"
                                            >
                                                <Button
                                                    id="closeOpenToolManager"
                                                    className="closeOpenToolManager"
                                                    onClick={() => setVisibleTool(null)}
                                                >
                                                    <img
                                                        id="closeOpenToolManagerImg"
                                                        className="closeOpenToolManagerImg"
                                                        src="/SVGicons/X-white.svg"
                                                        alt="Close"
                                                        style={{ width: '12px', height: '12px' }}
                                                    />
                                                </Button>
                                            </div>
                                        </div>



                                        <ToolTagManagerTool
                                            inputValue={inputValue}
                                            setInputValue={setInputValue}
                                            listItems={listItems}
                                            setListItems={setListItems}
                                            rows={rows}
                                            setRows={setRows}
                                            columns={columns}
                                            onTagCountChange={handleTagCountChange}
                                        />
                                    </div>


                                </div>

                                <div id="appBodyToolStyles"
                                    className={`col-md-12 gray ${visibleTool === 'styles' ? '' : 'd-none'}`}
                                >

                                    <div
                                        id="stylesManagerToolContainer"
                                        className="stylesManagerToolContainer"
                                        style={{ display: "none" }}
                                    >
                                        <div
                                            id="stylesManagerToolContainerTitleContainer"
                                            className="stylesManagerToolContainerTitleContainer"
                                        >
                                            <div
                                                id="stylesManagerToolContainerTitleContainerCol01IconBox"
                                                className="stylesManagerToolContainerTitleContainerCol01IconBox"
                                            >
                                                <img
                                                    id="stylesManagerToolContainerTitleBoxIconImg"
                                                    src="/SVGicons/styles-black.svg"
                                                    alt="Save Styles"
                                                    style={{ width: '24px', height: '24px' }}
                                                />
                                            </div>
                                            <div
                                                id="stylesManagerToolContainerTitleContainerCol02TitleBox"
                                                className="stylesManagerToolContainerTitleContainerCol02TitleBox"
                                            >
                                                <p
                                                    id="stylesManagerTitleViews"
                                                >
                                                    Styles Manager OLD
                                                </p>

                                            </div>
                                            <div
                                                id="stylesManagerToolContainerTitleContainerCol03ButtonBox"
                                                className="stylesManagerToolContainerTitleContainerCol03ButtonBox"
                                            >
                                                <Button
                                                    id="closeOpenToolManager"
                                                    className="closeOpenToolManager"
                                                    onClick={() => setVisibleTool(null)}
                                                >
                                                    <img
                                                        id="closeOpenToolManagerImg"
                                                        className="closeOpenToolManagerImg"
                                                        src="/SVGicons/X-white.svg"
                                                        alt="Close"
                                                        style={{ width: '12px', height: '12px' }}
                                                    />
                                                </Button>
                                            </div>
                                        </div>


                                        Styles
                                    </div>

                                </div>

                                <div id="appBodyToolFilters"
                                    className={`col-md-12 gray ${visibleTool === 'filters' ? '' : 'd-none'}`}
                                >
                                    <div
                                        id="filtersManagerToolContainer"
                                        className="filtersManagerToolContainer"
                                    >
                                        <div
                                            id="filtersManagerToolContainerTitleContainer"
                                            className="filtersManagerToolContainerTitleContainer"
                                        >
                                            <div
                                                id="filtersManagerToolContainerTitleContainerCol01IconBox"
                                                className="filtersManagerToolContainerTitleContainerCol01IconBox"
                                            >
                                                <img
                                                    id="filtersManagerToolContainerTitleBoxIconImg"
                                                    src="/SVGicons/filters-black.svg"
                                                    alt="Save Tag"
                                                    style={{ width: '24px', height: '24px' }}
                                                />
                                            </div>
                                            <div
                                                id="filtersManagerToolContainerTitleContainerCol02TitleBox"
                                                className="filtersManagerToolContainerTitleContainerCol02TitleBox"
                                            >
                                                <p
                                                    id="toolManagerTitleFilters"
                                                >
                                                    Filters Manager
                                                </p>

                                            </div>
                                            <div
                                                id="filtersManagerToolContainerTitleContainerCol03ButtonBox"
                                                className="filtersManagerToolContainerTitleContainerCol03ButtonBox"
                                            >
                                                <Button
                                                    id="closeOpenToolManager"
                                                    className="closeOpenToolManager"
                                                    onClick={() => setVisibleTool(null)}
                                                >
                                                    <img
                                                        id="closeOpenToolManagerImg"
                                                        className="closeOpenToolManagerImg"
                                                        src="/SVGicons/X-white.svg"
                                                        alt="Close"
                                                        style={{ width: '12px', height: '12px' }}
                                                    />
                                                </Button>
                                            </div>
                                        </div>

                                        <div id="managerFiltersComponent" className="componentScroll" style={{ overflowY: 'auto', maxHeight: '100vh' }}>
                                            <ManagerFilters
                                                triggerProjectReporter={triggerProjectReporter}
                                                tagsList={listItems}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
};

export default BootstrapStarterBody;
